import { Validator } from 'projects/core/src/lib/helpers/validator';
import { Injectable } from '@angular/core';
import { AuthStateService } from './auth-state.service';

const STANDARD_TIMEZONE = 'Europe/Berlin';

export enum TimezoneModes {
  Own = 'own',
  Procedure = 'procedure'
}

interface Timezone {
  id: string;
  timezone: string;
  offset: string;
}

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {

  private timezones: Array<Timezone> = [];
  private hasToShowTimezones = false;

  constructor(
    private auth: AuthStateService
  ) { }

  public setTimezones(data: any): void {
    this.timezones = [];
    if (data.timezone && data.tz_offset) {
      this.timezones.push({
        id: 'procedure',
        timezone: data.timezone,
        offset: data.tz_offset,
      });
    }
    if (data.moderator) {
      this.timezones.push({
        id: data.moderator.hashed_id,
        timezone: data.moderator.timezone,
        offset: data.moderator.tz_offset,
      });
    }
    if (Validator.isArray(data.candidates, 1)) {
      data.candidates.forEach((candidate: any) => {
        this.timezones.push({
          id: candidate.hashed_id,
          timezone: candidate.timezone,
          offset: candidate.tz_offset,
        });
      });
    }
    if (Validator.isArray(data.observers, 1)) {
      data.observers.forEach((observer: any) => {
        this.timezones.push({
          id: observer.hashed_id,
          timezone: observer.timezone,
          offset: observer.tz_offset,
        });
      });
    }
    this.checkForShowTimezones();
  }

  public setTimezonesFromHashs(data: any): void {
    Object.keys(data).forEach((hash: string) => {
      const entry = data[hash];
      this.setTimezone(hash, entry.timezone, entry.tz_offset);
    });
    this.checkForShowTimezones();
  }

  public setTimezoneMode(mode: string): void {
    localStorage.setItem('acr_runner_timezone_mode', mode);
  }

  public setTimezone(id: string, timezone: string, offset: string): void {
    const actual = this.getTimezone(id);
    if (actual) {
      actual.timezone = timezone;
      actual.offset = offset;
    }
    this.checkForShowTimezones();
  }

  public getTimezoneMode(): string {
    const mode = localStorage.getItem('acr_runner_timezone_mode');
    return (mode !== null) ? mode : TimezoneModes.Own;
  }

  public getActiveTimezone(full = false): string {
    let type: string = null;
    const mode = this.getTimezoneMode();
    if (mode === TimezoneModes.Procedure) {
      type = 'procedure';
    } else {
      type = this.auth.getHashedId();
    }
    const timezone = this.timezones.find((actual: Timezone) => {
      return actual.id === type;
    });
    const timezoneString = (timezone) ? timezone.timezone : STANDARD_TIMEZONE;
    if (full === true && timezone) {
      return this.getTimezoneString(timezone.id);
    }
    return timezoneString;
  }

  public getMyTimezone(): string {
    const timezone = this.timezones.find((actual: Timezone) => {
      return actual.id === this.auth.getHashedId();
    });
    const result = (timezone) ? timezone.timezone : STANDARD_TIMEZONE;
    return result;
  }

  public getTimezone(id: string): Timezone | null {
    return this.timezones.find((timezone: Timezone) => {
      return timezone.id === id;
    }) || null;
  }

  public getTimezoneString(id: string, full = true): string {
    const timezone = this.getTimezone(id);
    if (timezone) {
      return (full === true) ? timezone.timezone + ' (GMT' + timezone.offset + ')' : timezone.timezone;
    } else {
      return '';
    }
  }

  public showTimezones(): boolean {
    return this.hasToShowTimezones;
  }

  public showTimezoneSwitch(): boolean {
    const offsetMy = this.getTimezone(this.auth.getHashedId())?.offset;
    const offsetProcedure = this.getTimezone('procedure').offset;
    if (offsetMy && offsetProcedure) {
      return offsetMy !== offsetProcedure;
    }
    return this.getMyTimezone() !== this.getTimezone('procedure').timezone;
  }

  private checkForShowTimezones(): void {
    const timezones: Array<string> = [];
    this.timezones.forEach((actual: Timezone) => {
      if (timezones.indexOf(actual.timezone) === -1) {
        timezones.push(actual.timezone);
      }
    });
    this.hasToShowTimezones = (timezones.length > 1);
  }

  public getShowProcedureTimes(): boolean {
    return (localStorage.getItem('acr_runner_agenda_show_locale_time') === 'yes') || false;
  }

  public setShowProcedureTimes(mode: boolean): void {
    localStorage.setItem('acr_runner_agenda_show_locale_time', (mode === true) ? 'yes' : null);
  }

}
